import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  clearStoredToken,
  fetchBoardLink,
  fetchStudents,
  getStoredToken,
  MyBoardsApiError,
  teacherLogin,
  type Student,
} from "./myBoardsApi";

import "./MyBoardsPanel.scss";

type BoardLinkState =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error" };

type PanelState =
  | { view: "gate"; error: string | null; submitting: boolean }
  | { view: "loading" }
  | { view: "list"; students: Student[] }
  | { view: "error"; message: string };

const lockIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="10" width="14" height="10" rx="2.5" />
    <path
      d="M8 10V7a4 4 0 0 1 8 0v3"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

const searchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="10" cy="10" r="7" />
    <path d="M20 20l-4.5-4.5" />
  </svg>
);

const openArrowIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 17L17 7M9 7h8v8" />
  </svg>
);

export const MyBoardsPanel = () => {
  const [token, setTokenState] = useState<string | null>(() =>
    getStoredToken(),
  );
  const [state, setState] = useState<PanelState>(
    token ? { view: "loading" } : { view: "gate", error: null, submitting: false },
  );
  const [password, setPassword] = useState("");
  const [query, setQuery] = useState("");
  const [boardLinks, setBoardLinks] = useState<
    Record<number, BoardLinkState>
  >({});

  const bounceToGate = useCallback(() => {
    clearStoredToken();
    setTokenState(null);
    setBoardLinks({});
    setState({ view: "gate", error: null, submitting: false });
  }, []);

  const loadStudents = useCallback(
    async (authToken: string) => {
      setState({ view: "loading" });
      let students: Student[];
      try {
        students = await fetchStudents(authToken);
      } catch (error: any) {
        if (error instanceof MyBoardsApiError && error.status === 401) {
          bounceToGate();
          return;
        }
        setState({
          view: "error",
          message: error.message ?? "Не удалось загрузить список учеников",
        });
        return;
      }

      setState({ view: "list", students });

      // no bulk "students + board links" endpoint on the backend yet — fetch
      // each student's board link in parallel, one failure doesn't block
      // the rest (see rebrand plan, "Мои доски" section)
      setBoardLinks(
        Object.fromEntries(students.map((s) => [s.id, { status: "loading" }])),
      );
      await Promise.all(
        students.map(async (student) => {
          try {
            const url = await fetchBoardLink(authToken, student.id);
            setBoardLinks((prev) => ({
              ...prev,
              [student.id]: { status: "ready", url },
            }));
          } catch (error: any) {
            if (error instanceof MyBoardsApiError && error.status === 401) {
              bounceToGate();
              return;
            }
            setBoardLinks((prev) => ({
              ...prev,
              [student.id]: { status: "error" },
            }));
          }
        }),
      );
    },
    [bounceToGate],
  );

  useEffect(() => {
    if (token) {
      loadStudents(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (state.view !== "gate" || !password) {
      return;
    }
    setState({ view: "gate", error: null, submitting: true });
    try {
      const newToken = await teacherLogin(password);
      trackEvent("board", "my-boards-login");
      setPassword("");
      setTokenState(newToken);
    } catch (error: any) {
      setState({
        view: "gate",
        error: error.message ?? "Не удалось войти",
        submitting: false,
      });
    }
  };

  const openBoard = (student: Student) => {
    const link = boardLinks[student.id];
    if (link?.status !== "ready") {
      return;
    }
    trackEvent("board", "my-boards-open");
    window.open(link.url, "_blank", "noopener,noreferrer");
  };

  if (state.view === "gate") {
    return (
      <div className="my-boards-panel">
        <div className="my-boards-gate">
          <div className="my-boards-gate__icon">{lockIcon}</div>
          <div>
            <h3>Панель для репетиторов</h3>
            <p>
              Введите общий пароль репетитора, чтобы увидеть список учеников
              и их доски.
            </p>
          </div>
          <form className="my-boards-gate__form" onSubmit={handleSubmit}>
            <input
              type="password"
              placeholder="Пароль репетитора"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={state.error ? "has-error" : undefined}
            />
            {state.error && (
              <div className="my-boards-gate__error">{state.error}</div>
            )}
            <button
              type="submit"
              disabled={state.submitting || !password}
              className="my-boards-gate__submit"
            >
              {state.submitting ? "Входим…" : "Войти"}
            </button>
          </form>
          <div className="my-boards-gate__hint">
            Пароль выдаётся администратором Math From Zero
          </div>
        </div>
      </div>
    );
  }

  if (state.view === "error") {
    return (
      <div className="my-boards-panel">
        <div className="my-boards-error">
          <p>{state.message}</p>
          <button onClick={() => token && loadStudents(token)}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  if (state.view === "loading") {
    return (
      <div className="my-boards-panel">
        <div className="my-boards-loading">Загрузка…</div>
      </div>
    );
  }

  const filtered = state.students.filter((s) =>
    s.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className="my-boards-panel">
      <p className="my-boards-panel__count">
        {state.students.length}{" "}
        {pluralizeStudents(state.students.length)}
      </p>

      {state.students.length > 3 && (
        <div className="my-boards-search">
          {searchIcon}
          <input
            placeholder="Найти ученика..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      <div className="my-boards-list">
        {filtered.map((student) => {
          const link = boardLinks[student.id] ?? { status: "loading" };
          return (
            <div
              key={student.id}
              className={`my-boards-card${
                student.is_active ? "" : " inactive"
              }`}
            >
              <div className="my-boards-card__avatar">{student.emoji}</div>
              <div className="my-boards-card__body">
                <p className="my-boards-card__name">{student.name}</p>
                <span
                  className={`my-boards-card__status${
                    student.is_active ? " active" : " inactive"
                  }`}
                >
                  <span className="dot" />
                  {student.is_active ? "Занимается" : "Занятия приостановлены"}
                </span>
              </div>
              <button
                className="my-boards-card__open"
                disabled={link.status !== "ready"}
                onClick={() => openBoard(student)}
              >
                {link.status === "loading" ? "…" : "Открыть"}
                {link.status === "ready" && openArrowIcon}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const pluralizeStudents = (count: number): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "ученик";
  }
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return "ученика";
  }
  return "учеников";
};
